import {
  Button,
  EmptyState,
  ErrorBanner,
  TextField,
} from "frontend-kit/components/index.js";
import { normalizeCommitmentHex } from "./commitment-route.js";
import {
  addSavedAgent,
  loadSavedAgents,
  removeSavedAgent,
} from "./agents-store.js";
import { maskCommitment } from "./privacy-copy.js";
import { icon } from "./icons.js";

const AMBIT_DOCS_URL = "https://docs.nocturne-standards.org/v1/ambit/";

/**
 * @param {{ log?: Function, onMonitor?: Function }} opts
 */
export function renderAgentsPage({ log, onMonitor }) {
  const root = document.createElement("div");
  root.className = "agents-page";

  const header = document.createElement("div");
  header.className = "agents-header";
  const h1 = document.createElement("h1");
  h1.textContent = "Agents";
  header.append(h1);
  root.append(header);

  const intro = document.createElement("section");
  intro.className = "agents-intro dt-card";
  const introCopy = document.createElement("p");
  introCopy.className = "hint";
  introCopy.textContent =
    "Ambit agents are market participants funded by Chit allowance. Monitor a commitment's deployed balance, fees, and open positions - read-only in this portal.";
  const docsLink = document.createElement("a");
  docsLink.href = AMBIT_DOCS_URL;
  docsLink.target = "_blank";
  docsLink.rel = "noopener noreferrer";
  docsLink.className = "link-btn";
  docsLink.textContent = "Ambit specification";
  intro.append(introCopy, docsLink);
  root.append(intro);

  const lookup = document.createElement("section");
  lookup.className = "agents-lookup dt-card";
  const lookupTitle = document.createElement("h2");
  lookupTitle.className = "agents-section-title";
  lookupTitle.textContent = "Monitor commitment";
  lookup.append(lookupTitle);

  let inputValue = "";
  const errHost = document.createElement("div");
  lookup.append(errHost);

  const field = TextField({
    id: "agents-commitment-input",
    label: "Commitment hex (64 digits)",
    value: "",
    onInput: (v) => {
      inputValue = v;
      errHost.replaceChildren();
    },
  });
  lookup.append(field);

  const actions = document.createElement("div");
  actions.className = "agents-lookup-actions state-row";
  actions.append(
    Button({
      label: "Monitor",
      variant: "primary",
      onClick: () => {
        try {
          const hex = normalizeCommitmentHex(inputValue);
          onMonitor?.(hex);
          location.hash = `#/agent/${hex}`;
        } catch (e) {
          errHost.replaceChildren(ErrorBanner({ message: e.message || String(e) }));
        }
      },
    }),
    Button({
      label: "Save to list",
      variant: "secondary",
      onClick: () => {
        try {
          const hex = normalizeCommitmentHex(inputValue);
          addSavedAgent(hex);
          paintSaved();
          log?.(`saved agent ${hex.slice(0, 8)}…`);
        } catch (e) {
          errHost.replaceChildren(ErrorBanner({ message: e.message || String(e) }));
        }
      },
    })
  );
  lookup.append(actions);
  root.append(lookup);

  const savedSection = document.createElement("section");
  savedSection.className = "agents-saved dt-card";
  const savedTitle = document.createElement("h2");
  savedTitle.className = "agents-section-title";
  savedTitle.textContent = "Saved agents";
  savedSection.append(savedTitle);
  const savedHost = document.createElement("div");
  savedSection.append(savedHost);
  root.append(savedSection);

  function paintSaved() {
    savedHost.replaceChildren();
    const saved = loadSavedAgents();
    if (!saved.length) {
      savedHost.append(
        EmptyState({
          title: "No saved agents",
          description: "Save commitments you monitor often for quick access.",
        })
      );
      return;
    }
    const list = document.createElement("ul");
    list.className = "agents-saved-list";
    for (const row of saved) {
      const li = document.createElement("li");
      li.className = "agents-saved-item";
      const link = document.createElement("button");
      link.type = "button";
      link.className = "link-btn mono";
      link.textContent = maskCommitment(row.commitment_hex);
      link.title = row.commitment_hex;
      link.addEventListener("click", () => {
        location.hash = `#/agent/${row.commitment_hex}`;
      });
      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "icon-btn";
      removeBtn.title = "Remove";
      removeBtn.setAttribute("aria-label", "Remove saved agent");
      removeBtn.append(icon("alert", { size: 14 }));
      removeBtn.addEventListener("click", () => {
        removeSavedAgent(row.commitment_hex);
        paintSaved();
      });
      li.append(link, removeBtn);
      list.append(li);
    }
    savedHost.append(list);
  }

  paintSaved();
  return root;
}
